import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import uuid from 'uuid-random';
import { normalizeBffAutocompleteResponseToLegacy } from './placesBffNormalize';
import { toIncludedRegionCodesUpper } from './googleMapsPlacesMapping';
import { useGoogleMapsAPI } from './useGoogleMapsAPI';

const PLACES_AUTOCOMPLETE_PATH = '/publication/api/v1/places/autocomplete';
const DEFAULT_DEBOUNCE_MS = 350;

function resolvePublicationBaseUrl(publicationApiBaseUrl) {
  if (publicationApiBaseUrl) return String(publicationApiBaseUrl).replace(/\/$/, '');
  if (typeof process !== 'undefined') {
    const fromEnv =
      process.env.REACT_APP_PUBLICATION_API_BASE ||
      process.env.REACT_APP_API_PUBLICADOR_PROF ||
      process.env.REACT_APP_API_PUBLICADOR ||
      '';
    return String(fromEnv).replace(/\/$/, '');
  }
  return '';
}

/**
 * Autocomplete y detalle de Places vía BFF (`realestate-posting-publication-api`).
 * Compone {@link useGoogleMapsAPI} para `geocode` y `resetSession` del token de sesión de Google.
 *
 * No envía `includedPrimaryTypes` (paridad con AVISOS-6014 / sin filtro de tipos en el cliente).
 *
 * `ready`: base de publication configurada (HTTP autocomplete). El consumidor suele combinarlo con
 * `isScriptLoaded` del bootstrap para Geocoder/mapa; las sugerencias BFF no requieren la librería Places.
 *
 * @param {Object} [options]
 * @param {Object} [options.componentRestrictions] - `{ country: 'ar' }` → `includedRegionCodes` al BFF
 * @param {number} [options.debounceMs]
 * @param {boolean} [options.isScriptLoaded]
 * @param {string} [options.publicationApiBaseUrl] - override; por defecto env del panel (publication / prof)
 */
export function useGoogleMapsPlacesBff({
  componentRestrictions,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  isScriptLoaded = true,
  publicationApiBaseUrl
} = {}) {
  const baseUrl = resolvePublicationBaseUrl(publicationApiBaseUrl);
  const siteId = typeof process !== 'undefined' ? process.env.REACT_APP_BUILD_SITE || 'ZPAR' : 'ZPAR';

  const base = useGoogleMapsAPI({ componentRestrictions, debounceMs, isScriptLoaded });

  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const bffSessionTokenRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const autocompleteRequestIdRef = useRef(0);

  const ensureBffSessionToken = useCallback(() => {
    if (!bffSessionTokenRef.current) {
      bffSessionTokenRef.current = uuid();
    }
    return bffSessionTokenRef.current;
  }, []);

  const resetBffSession = useCallback(() => {
    bffSessionTokenRef.current = null;
  }, []);

  const resetSession = useCallback(() => {
    resetBffSession();
    base.resetSession();
  }, [base, resetBffSession]);

  const bffReady = Boolean(baseUrl);

  const searchImmediate = useCallback(
    async input => {
      if (!baseUrl) {
        setPredictions([]);
        return { predictions: [], status: 'NOT_READY' };
      }

      setLoading(true);
      setError(null);

      const includedRegionCodes = toIncludedRegionCodesUpper(componentRestrictions);
      const sessionToken = ensureBffSessionToken();
      const body = {
        input,
        sessionToken,
        ...(includedRegionCodes?.length ? { includedRegionCodes } : {})
      };

      const requestId = ++autocompleteRequestIdRef.current;

      try {
        const { data } = await axios.post(`${baseUrl}${PLACES_AUTOCOMPLETE_PATH}`, body, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            'x-panel-portal': siteId,
            sessionId: Cookies.get('sessionId') || ''
          }
        });

        if (requestId !== autocompleteRequestIdRef.current) {
          setLoading(false);
          return { predictions: [], status: 'STALE' };
        }

        const legacyPredictions = normalizeBffAutocompleteResponseToLegacy(data);

        setPredictions(legacyPredictions);
        setLoading(false);
        // El BFF informa explícitamente `status: 'ZERO_RESULTS'` (200 OK + suggestions: []).
        // Respetamos ese contrato y caemos al length solo como fallback defensivo.
        const bffStatus = typeof data?.status === 'string' ? data.status : null;
        const status =
          bffStatus === 'ZERO_RESULTS' || legacyPredictions.length === 0 ? 'ZERO_RESULTS' : bffStatus || 'OK';
        return { predictions: legacyPredictions, status };
      } catch (err) {
        setLoading(false);
        setPredictions([]);
        const code = err?.response?.status ?? err?.code;
        // Contrato del BFF: el caso "sin resultados" se devuelve como 200 + status='ZERO_RESULTS'.
        // Por lo tanto un 404 acá es siempre un error real (path/deploy mal configurado) y no
        // se debe enmascarar como vacío: lo distinguimos para alertar y reportar a NR.
        let status;
        if (code === 404) {
          status = 'NOT_FOUND';
          setError('PLACES_AUTOCOMPLETE_NOT_FOUND');
          if (typeof window !== 'undefined' && window.newrelic?.noticeError) {
            try {
              window.newrelic.noticeError(err instanceof Error ? err : new Error('Places autocomplete not found'), {
                section: 'places.bff.autocomplete',
                portal: siteId,
                httpStatus: 404
              });
            } catch (_) {
              // no-op: no romper el flujo si NR no está disponible
            }
          }
        } else if (code === 'ZERO_RESULTS') {
          status = 'ZERO_RESULTS';
        } else {
          status = code || 'UNKNOWN_ERROR';
          setError(status);
        }
        return { predictions: [], status };
      }
    },
    [baseUrl, componentRestrictions, ensureBffSessionToken, siteId]
  );

  const search = useCallback(
    input => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      const trimmedInput = input?.trim();
      if (!trimmedInput) {
        setPredictions([]);
        setError(null);
        return Promise.resolve({ predictions: [], status: 'EMPTY' });
      }
      return new Promise(resolve => {
        debounceTimerRef.current = setTimeout(() => {
          debounceTimerRef.current = null;
          searchImmediate(trimmedInput).then(resolve);
        }, debounceMs);
      });
    },
    [debounceMs, searchImmediate]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const getDetails = useCallback(
    async placeId => {
      if (!baseUrl) {
        setError('NOT_READY');
        throw new Error('Publication API base URL no configurada.');
      }
      if (!placeId) {
        setError('INVALID_PLACE_ID');
        throw new Error('placeId inválido.');
      }

      setLoading(true);
      setError(null);

      const sessionToken = ensureBffSessionToken();
      const encodedId = encodeURIComponent(placeId);

      try {
        const { data } = await axios.get(`${baseUrl}/publication/api/v1/places/${encodedId}`, {
          withCredentials: true,
          params: { sessionToken },
          headers: {
            'Content-Type': 'application/json',
            'x-panel-portal': siteId,
            sessionId: Cookies.get('sessionId') || ''
          }
        });

        const raw = data?.raw ?? {};
        const lat = data?.lat ?? raw?.geometry?.location?.lat ?? null;
        const lng = data?.lng ?? raw?.geometry?.location?.lng ?? null;

        const normalizedRaw = {
          ...raw,
          place_id: data?.placeId ?? raw.place_id ?? placeId,
          formatted_address: raw.formatted_address ?? data?.address ?? '',
          name: raw.name ?? data?.name ?? '',
          geometry:
            raw.geometry ??
            (lat != null && lng != null
              ? {
                  location: {
                    lat: () => lat,
                    lng: () => lng
                  }
                }
              : null),
          address_components: raw.address_components ?? []
        };

        setLoading(false);
        return {
          placeId: data?.placeId ?? placeId,
          address: data?.address ?? '',
          name: data?.name ?? '',
          lat,
          lng,
          raw: normalizedRaw
        };
      } catch (err) {
        setLoading(false);
        const status = err?.response?.status || 'UNKNOWN_ERROR';
        setError(status);
        throw new Error(err?.response?.data?.message || err?.message || String(status));
      }
    },
    [baseUrl, ensureBffSessionToken, siteId]
  );

  return {
    ready: bffReady,
    loading,
    predictions,
    search,
    getDetails,
    geocode: base.geocode,
    resetSession,
    error: error ?? base.error
  };
}
